import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import { useTranslation } from "../../../utils/translation";
import {
  createUserByAdmin,
  listUsers,
  selectUsersCreateError,
  selectUsersCreateLoading,
  selectUsersCreateMessage,
} from "../userSlice";

const UsersCreatePage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const createLoading = useSelector(selectUsersCreateLoading);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);
  const createError = useSelector(selectUsersCreateError);
  const createMessage = useSelector(selectUsersCreateMessage);
  const [successMessage, setSuccessMessage] = useState("");
  const [permissionError, setPermissionError] = useState("");
  const [formState, setFormState] = useState({
    name: "",
    surName: "",
    email: "",
    phoneNumber: "",
    tempPassword: "",
    userName: "",
    businessId: "",
    businessName: "",
    about: "",
    businessPosition: "",
    job: "",
    city: "",
    location: "",
    country: "",
    language: "",
  });

  const buildMailStatusSuffix = (result) => {
    const status = result?.mailStatus;
    if (status === "sent") return " Kullanıcıya mail gönderilmiştir.";
    if (status === "failed") return " Kullanıcı oluşturuldu, ancak mail gönderilemedi.";
    if (status === "skipped") return " Kullanıcı oluşturuldu, ancak e-posta adresi olmadığı için mail gönderilmedi.";
    return "";
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!adminAccess) {
      setPermissionError(t("admin_common.permission_denied"));
      return;
    }
    setSuccessMessage("");
    setPermissionError("");
    const payload = {
      name: formState.name?.trim() || "",
      surName: formState.surName?.trim() || "",
      email: formState.email?.trim() || "",
      phoneNumber: formState.phoneNumber?.trim() || "",
      tempPassword: formState.tempPassword?.trim() || "",
      userName: formState.userName?.trim() || "",
      businessId: formState.businessId?.trim() || "",
      businessName: formState.businessName?.trim() || "",
      about: formState.about?.trim() || "",
      businessPosition: formState.businessPosition?.trim() || "",
      job: formState.job?.trim() || "",
      city: formState.city?.trim() || "",
      location: formState.location?.trim() || "",
      country: formState.country?.trim() || "",
      language: formState.language?.trim() || "",
    };
    if (!payload.name || !payload.surName || !payload.email || !payload.tempPassword) {
      swal(
        t("admin_users_pages.missing_fields_title"),
        "Ad, Soyad, E-posta ve Geçici Şifre zorunludur.",
        "error"
      );
      return;
    }
    if (payload.tempPassword.length < 6) {
      swal(
        t("admin_users_pages.missing_fields_title"),
        t("admin_users_pages.password_length_message"),
        "error"
      );
      return;
    }
    try {
      const result = await dispatch(createUserByAdmin(payload)).unwrap();
      await dispatch(listUsers());
      setSuccessMessage(`${t("admin_users_pages.create_success_message")}${buildMailStatusSuffix(result)}`);
      setFormState({
        name: "",
        surName: "",
        email: "",
        phoneNumber: "",
        tempPassword: "",
        userName: "",
        businessId: "",
        businessName: "",
        about: "",
        businessPosition: "",
        job: "",
        city: "",
        location: "",
        country: "",
        language: "",
      });
    } catch (error) {
      // Error is handled in slice state
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
            <h4 className="card-title">{t("admin_users_pages.create_title")}</h4>
            {permissionError && (
              <div className="alert alert-danger">{permissionError}</div>
            )}
            {successMessage && (
              <div className="alert alert-success">{successMessage}</div>
            )}
            {createError && (
              <div className="text-danger mb-2">
                {createMessage || t("admin_users_pages.create_failed")}
              </div>
              )}
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="text-black font-w500">Ad (Name)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={formState.name}
                      onChange={handleChange}
                      placeholder="Ad"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="text-black font-w500">Soyad (Surname)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="surName"
                      value={formState.surName}
                      onChange={handleChange}
                      placeholder="Soyad"
                    />
                  </div>
                </div>
              </div>
              <div className="form-group mb-3">
                <label className="text-black font-w500">{t("admin_common.email")}</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={formState.email}
                  onChange={handleChange}
                  placeholder={t("admin_common.email")}
                />
              </div>
              <div className="form-group mb-3">
                <label className="text-black font-w500">Telefon (Phone)</label>
                <input
                  type="tel"
                  className="form-control"
                  name="phoneNumber"
                  value={formState.phoneNumber}
                  onChange={handleChange}
                  placeholder="+90 555 555 55 55"
                />
              </div>
              <div className="form-group mb-3">
                <label className="text-black font-w500">{t("admin_users_pages.temp_password")}</label>
                <input
                  type="password"
                  className="form-control"
                  name="tempPassword"
                  value={formState.tempPassword}
                  onChange={handleChange}
                  placeholder={t("admin_users_pages.temp_password")}
                />
              </div>
              <div className="form-group mb-3">
                <label className="text-black font-w500">Kullanıcı Adı (Username)</label>
                <input
                  type="text"
                  className="form-control"
                  name="userName"
                  value={formState.userName}
                  onChange={handleChange}
                  placeholder="kullanici_adi"
                />
              </div>
              <div className="form-group mb-3">
                <label className="text-black font-w500">Şirket Adı (Business Name)</label>
                <input
                  type="text"
                  className="form-control"
                  name="businessName"
                  value={formState.businessName}
                  onChange={handleChange}
                  placeholder="Şirket Adı"
                />
              </div>
              <div className="form-group mb-3">
                <label className="text-black font-w500">Pozisyon (Business Position)</label>
                <input
                  type="text"
                  className="form-control"
                  name="businessPosition"
                  value={formState.businessPosition}
                  onChange={handleChange}
                  placeholder="Genel Müdür, CEO, Yönetici vb."
                />
              </div>
              <div className="form-group mb-3">
                <label className="text-black font-w500">Meslek (Job)</label>
                <input
                  type="text"
                  className="form-control"
                  name="job"
                  value={formState.job}
                  onChange={handleChange}
                  placeholder="Meslek"
                />
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="text-black font-w500">Şehir (City)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="city"
                      value={formState.city}
                      onChange={handleChange}
                      placeholder="İstanbul, Ankara vb."
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="text-black font-w500">Ülke (Country)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="country"
                      value={formState.country}
                      onChange={handleChange}
                      placeholder="Türkiye, USA vb."
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="text-black font-w500">Lokasyon (Location)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="location"
                      value={formState.location}
                      onChange={handleChange}
                      placeholder="Kadıköy, Ataşehir vb."
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="text-black font-w500">Dil (Language)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="language"
                      value={formState.language}
                      onChange={handleChange}
                      placeholder="tr, en vb."
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label className="text-black font-w500">Hakkında (About)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="about"
                      value={formState.about}
                      onChange={handleChange}
                      placeholder="Kısa açıklama"
                    />
                  </div>
                </div>
              </div>
              <div className="form-group mb-3">
                <label className="text-black font-w500">{t("admin_users_pages.business_id_optional")}</label>
                <input
                  type="text"
                  className="form-control"
                  name="businessId"
                  value={formState.businessId}
                  onChange={handleChange}
                  placeholder={t("admin_common.business_id")}
                />
              </div>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={createLoading}
              >
                {t("admin_common.save")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UsersCreatePage;
